if (!customElements.get('maxtiz-size-help')) {
  customElements.define('maxtiz-size-help', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.sizePicker = this.querySelector('[data-shoe-size-picker]');
      this.footLengthInput = this.querySelector('[data-foot-length-input]');
      this.footLengthFeedback = this.querySelector('[data-foot-length-feedback]');
      const trigger = this.querySelector('.mx-size-guide__trigger');
      this.querySelectorAll('[data-size-region]').forEach((button) => {
        button.addEventListener('click', () => this.selectRegion(button.dataset.sizeRegion));
      });
      this.querySelectorAll('[data-shoe-system]').forEach((button) => {
        button.addEventListener('click', () => this.selectShoeSystem(button.dataset.shoeSystem));
      });
      this.querySelectorAll('[data-shoe-gender]').forEach((button) => {
        button.addEventListener('click', () => this.selectShoeGender(button.dataset.shoeGender));
      });
      this.querySelectorAll('[data-size-unit]').forEach((button) => {
        button.addEventListener('click', () => this.selectUnit(button.dataset.sizeUnit));
      });
      this.querySelectorAll('.mx-size-guide__segments').forEach((group) => {
        group.addEventListener('keydown', (event) => this.navigateSegment(event, group));
      });
      this.sizePicker?.addEventListener('change', () => this.selectSizeRow(this.sizePicker.value));
      this.footLengthInput?.addEventListener('input', () => this.recommendFromFootLength());
      this.querySelectorAll('[data-shoe-gender-panel]').forEach((panel) => {
        [...panel.querySelectorAll('.mx-size-guide__shoe-table tbody tr')].forEach((row, index) => {
          row.tabIndex = 0;
          row.addEventListener('click', () => this.selectSizeRow(String(index)));
          row.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.selectSizeRow(String(index));
          });
        });
      });
      const initialGender = this.querySelector('[data-shoe-gender].is-active');
      if (initialGender) this.selectShoeGender(initialGender.dataset.shoeGender);
      const initialShoeSystem = this.querySelector('[data-shoe-system].is-active');
      if (initialShoeSystem) this.selectShoeSystem(initialShoeSystem.dataset.shoeSystem);
      const initialUnit = this.querySelector('[data-size-unit].is-active');
      if (initialUnit) this.selectUnit(initialUnit.dataset.sizeUnit);
      this.querySelector('.mx-size-guide__close').addEventListener('click', () => this.dialog.close());
      trigger.addEventListener('click', () => {
        this.previousOverflow = document.documentElement.style.overflow;
        this.dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
            event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow || '';
        trigger.focus({ preventScroll: true });
      });
    }
    disconnectedCallback() {
      if (this.dialog?.open) {
        document.documentElement.style.overflow = this.previousOverflow || '';
        this.dialog.close();
      }
    }
    selectRegion(region) {
      this.querySelectorAll('[data-size-region]').forEach((button) => {
        const isActive = button.dataset.sizeRegion === region;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      this.querySelectorAll('[data-size-region-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.sizeRegionPanel !== region;
      });
    }
    selectShoeSystem(system) {
      this.querySelectorAll('[data-shoe-system]').forEach((button) => {
        const isActive = button.dataset.shoeSystem === system;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });
      this.querySelectorAll('[data-size-system]').forEach((cell) => {
        cell.classList.toggle('is-selected', cell.dataset.sizeSystem === system);
      });
      this.querySelectorAll('.mx-size-guide__shoe-table-wrap').forEach((tableWrap) => {
        tableWrap.dataset.activeSystem = system;
      });
      this.activeShoeSystem = system;
      this.populateSizePicker();
    }
    selectShoeGender(gender) {
      this.querySelectorAll('[data-shoe-gender]').forEach((button) => {
        const isActive = button.dataset.shoeGender === gender;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });
      this.querySelectorAll('[data-shoe-gender-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.shoeGenderPanel !== gender;
      });
      this.activeShoeGender = gender;
      this.selectedShoeRow = undefined;
      this.querySelectorAll('.mx-size-guide__shoe-table tbody tr').forEach((row) => row.classList.remove('is-size-selected'));
      const result = this.querySelector('[data-size-result]');
      if (result) result.hidden = true;
      const usLabel = this.querySelector('[data-us-result-label]');
      if (usLabel) usLabel.textContent = gender === 'women' ? 'US Women' : 'US Men';
      this.populateSizePicker();
      if (this.measuredFootLengthCm !== undefined) this.applyFootLengthRecommendation();
    }
    selectUnit(unit) {
      this.querySelectorAll('[data-size-unit]').forEach((button) => {
        const isActive = button.dataset.sizeUnit === unit;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });
      this.querySelectorAll('[data-unit-value]').forEach((value) => {
        value.hidden = value.dataset.unitValue !== unit;
      });
      this.querySelectorAll('[data-unit-label]').forEach((label) => {
        label.textContent = unit.toUpperCase();
      });
      this.activeUnit = unit;
      this.updateFootLengthInput();
      if (this.selectedShoeRow !== undefined) this.updateSizeResult();
    }
    populateSizePicker() {
      if (!this.sizePicker || !this.activeShoeSystem) return;
      const previousRow = this.selectedShoeRow;
      const shoeRows = this.getActiveShoeRows();
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Choose a size';
      const options = shoeRows.map((row, index) => {
        const option = document.createElement('option');
        option.value = String(index);
        option.textContent = row.querySelector(`[data-size-system="${this.activeShoeSystem}"]`)?.textContent.trim() || '-';
        return option;
      });
      this.sizePicker.replaceChildren(placeholder, ...options);
      if (previousRow !== undefined) this.sizePicker.value = String(previousRow);
    }
    selectSizeRow(indexValue, source = 'manual') {
      const index = Number(indexValue);
      const shoeRows = this.getActiveShoeRows();
      const hasSelection = indexValue !== '' && Number.isInteger(index) && shoeRows[index];
      if (source !== 'measurement') this.clearFootMeasurement();
      this.querySelectorAll('.mx-size-guide__shoe-table tbody tr').forEach((row) => row.classList.remove('is-size-selected'));
      shoeRows.forEach((row, rowIndex) => row.classList.toggle('is-size-selected', hasSelection && rowIndex === index));
      const result = this.querySelector('[data-size-result]');
      if (!hasSelection) {
        this.selectedShoeRow = undefined;
        if (result) result.hidden = true;
        return;
      }
      this.selectedShoeRow = index;
      if (this.sizePicker) this.sizePicker.value = String(index);
      this.updateSizeResult();
      shoeRows[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    recommendFromFootLength() {
      if (!this.footLengthInput) return;
      const enteredValue = Number.parseFloat(this.footLengthInput.value);
      if (!Number.isFinite(enteredValue)) {
        this.measuredFootLengthCm = undefined;
        this.clearSizeSelection();
        this.footLengthInput.removeAttribute('aria-invalid');
        this.updateFootLengthFeedback();
        return;
      }
      this.measuredFootLengthCm = this.activeUnit === 'in' ? enteredValue * 2.54 : enteredValue;
      this.applyFootLengthRecommendation();
    }
    applyFootLengthRecommendation() {
      const shoeRows = this.getActiveShoeRows();
      if (!shoeRows.length || this.measuredFootLengthCm === undefined) return;
      const lengths = shoeRows.map((row) => Number.parseFloat(row.querySelector('[data-unit-value="cm"]')?.textContent));
      const min = lengths[0];
      const max = lengths[lengths.length - 1];
      if (this.measuredFootLengthCm < min || this.measuredFootLengthCm > max) {
        this.clearSizeSelection();
        if (this.footLengthInput) this.footLengthInput.setAttribute('aria-invalid', 'true');
        this.updateFootLengthFeedback(`Available range: ${this.formatLength(min)}-${this.formatLength(max)} ${this.activeUnit.toUpperCase()}.`);
        return;
      }
      const index = lengths.findIndex((length) => length + 0.001 >= this.measuredFootLengthCm);
      this.selectSizeRow(String(index), 'measurement');
      if (this.footLengthInput) this.footLengthInput.setAttribute('aria-invalid', 'false');
      const selectedCm = lengths[index];
      const roundedUp = selectedCm - this.measuredFootLengthCm > 0.01;
      const message = roundedUp
        ? `Between sizes - rounded up to ${this.formatLength(selectedCm)} ${this.activeUnit.toUpperCase()}.`
        : `Matched at ${this.formatLength(selectedCm)} ${this.activeUnit.toUpperCase()}.`;
      this.updateFootLengthFeedback(message, true);
    }
    updateFootLengthInput() {
      if (!this.footLengthInput || !this.activeUnit) return;
      const isInches = this.activeUnit === 'in';
      this.footLengthInput.min = isInches ? '8.46' : '21.5';
      this.footLengthInput.max = isInches ? '11.42' : '29';
      this.footLengthInput.step = isInches ? '0.01' : '0.1';
      this.footLengthInput.placeholder = isInches ? 'e.g. 9.57' : 'e.g. 24.3';
      if (this.measuredFootLengthCm !== undefined) {
        this.footLengthInput.value = this.formatLength(this.measuredFootLengthCm);
        this.applyFootLengthRecommendation();
      } else {
        this.updateFootLengthFeedback();
      }
    }
    formatLength(cmValue) {
      const value = this.activeUnit === 'in' ? cmValue / 2.54 : cmValue;
      return value.toFixed(this.activeUnit === 'in' ? 2 : 1).replace(/\.0$/, '');
    }
    clearFootMeasurement() {
      this.measuredFootLengthCm = undefined;
      if (this.footLengthInput) {
        this.footLengthInput.value = '';
        this.footLengthInput.removeAttribute('aria-invalid');
      }
      this.updateFootLengthFeedback();
    }
    clearSizeSelection() {
      this.selectedShoeRow = undefined;
      this.querySelectorAll('.mx-size-guide__shoe-table tbody tr').forEach((row) => row.classList.remove('is-size-selected'));
      if (this.sizePicker) this.sizePicker.value = '';
      const result = this.querySelector('[data-size-result]');
      if (result) result.hidden = true;
    }
    updateFootLengthFeedback(message, success = false) {
      if (!this.footLengthFeedback) return;
      this.footLengthFeedback.textContent = message || (this.activeUnit === 'in' ? 'Enter 8.46-11.42 in' : 'Enter 21.5-29 cm');
      this.footLengthFeedback.classList.toggle('is-success', success);
    }
    updateSizeResult() {
      const row = this.getActiveShoeRows()[this.selectedShoeRow];
      const result = this.querySelector('[data-size-result]');
      if (!row || !result) return;
      ['us', 'eu', 'uk'].forEach((system) => {
        const output = result.querySelector(`[data-size-result-value="${system}"]`);
        if (output) output.textContent = row.querySelector(`[data-size-system="${system}"]`)?.textContent.trim() || '-';
      });
      const footOutput = result.querySelector('[data-size-result-value="foot"]');
      if (footOutput) footOutput.textContent = row.querySelector(`[data-unit-value="${this.activeUnit || 'cm'}"]`)?.textContent.trim() || '-';
      result.hidden = false;
    }
    getActiveShoeRows() {
      const panel = this.querySelector(`[data-shoe-gender-panel="${this.activeShoeGender || 'women'}"]`);
      return panel ? [...panel.querySelectorAll('.mx-size-guide__shoe-table tbody tr')] : [];
    }
    navigateSegment(event, group) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const buttons = [...group.querySelectorAll('.mx-size-guide__segment')];
      if (!buttons.length) return;
      event.preventDefault();
      const currentIndex = Math.max(0, buttons.indexOf(document.activeElement));
      let nextIndex = event.key === 'Home' ? 0 : buttons.length - 1;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % buttons.length;
      buttons[nextIndex].focus();
      buttons[nextIndex].click();
    }
  });
}
